import { AppDispatch, RootState } from '../../redux/store/store';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { useDispatch, useSelector } from 'react-redux';
import './index.css';

import Loader from '../common/Loader';
import { RTU } from '../../types/RTU';
import RTUFilter from './RTUFilter';
import RTUList from './RTUList';
import RTUMap from './RTUMap';
import { fetchRTUs } from '../../redux/slices/rtuSlice';

// import RTUMap from './RTUMap';



// const RTUMap = React.lazy(() => import('./RTUMap'));

const MapViewPage: React.FC = () => {
  const { rtus } = useSelector((state: RootState) => state.rtu || {});
  const [, setSearchQuery] = useState<string>('');
  const [currentRTU, setCurrentRTU] = useState<RTU | undefined>();
  const [hoveredRTU, setHoveredRTU] = useState<RTU | null>(null);
  const [rtuSearchResult, setRtuSearchResult] = useState<RTU[]>([]);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)
  const [rtusInBounds, setRtusInBounds] = useState<RTU[]>([]);
  const [mapBoundsChanged, setMapBoundsChanged] = useState<boolean>(false);
  // Bugs 3172/3173/3174: track whether the user has applied a filter explicitly,
  // instead of inferring "no filter" from an empty result. A legitimate zero-match
  // filter (invalid Location, impossible CPU/Disk threshold) must survive the
  // WebSocket-driven `rtus` updates that re-fire the sync effect below.
  const [hasAppliedFilter, setHasAppliedFilter] = useState<boolean>(false);


  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // When component mounts, fetch data
    dispatch(fetchRTUs({})).then(() => {
      // Set state to indicate data has been loaded
      setDataLoaded(true);
    });
  }, [dispatch]);

  useEffect(() => {
    // Keep the working set mirrored to the live fleet only until the user applies
    // a filter. Once a filter is applied, do not overwrite the (possibly empty)
    // filtered result when `rtus` changes via WebSocket updates.
    if (rtus && !hasAppliedFilter) {
      setRtuSearchResult(rtus);
   }
  }, [rtus, hasAppliedFilter]);

 
  // const setFilters = useCallback((newFilters: any) => {
  //   const filteredRTUs = rtus.filter((rtu) => {
  //     return (
  //       (!newFilters.status || rtu.status === newFilters.status) &&
  //       (!newFilters.cpuUsage || (rtu.cpuUsage >= newFilters.cpuUsage[0] && rtu.cpuUsage <= newFilters.cpuUsage[1])) &&
  //       (!newFilters.diskUsage || (rtu.diskUsage >= newFilters.diskUsage[0] && rtu.diskUsage <= newFilters.diskUsage[1])) &&
  //       (!newFilters.location || rtu.location.toLowerCase().includes(newFilters.location.toLowerCase())) &&
  //       (!newFilters.client || rtu.client.toLowerCase().includes(newFilters.client.toLowerCase()))
  //     );
  //   });
  
  //   setRtuSearchResult(filteredRTUs);
  //   setMapBoundsChanged(false);

  // }, [rtus]);
  
  const setFilters = useCallback((newFilters: any) => {
    const filteredRTUs = rtus.filter((rtu) => {
      return (
        (!newFilters.status || newFilters.status === 'all' || rtu.status === newFilters.status) &&
        // Assuming newFilters.cpuUsage is now an object like { comparison: '>', value: 20 }
        (!newFilters.cpuUsage ||
          (newFilters.cpuUsage.comparison === '>' ? rtu.cpuUsage > newFilters.cpuUsage.value :
           newFilters.cpuUsage.comparison === '<' ? rtu.cpuUsage < newFilters.cpuUsage.value : true)) &&
        // Assuming newFilters.diskUsage is now an object like { comparison: '>', value: 50 }
        (!newFilters.diskUsage ||
          (newFilters.diskUsage.comparison === '>' ? rtu.diskUsage > newFilters.diskUsage.value :
           newFilters.diskUsage.comparison === '<' ? rtu.diskUsage < newFilters.diskUsage.value : true)) &&
        (!newFilters.location || rtu.location.toLowerCase().includes(newFilters.location.toLowerCase())) &&
        (!newFilters.producer || (rtu.provider || '').toLowerCase().includes(newFilters.producer.toLowerCase())) &&
        (!newFilters.field || (rtu.field || '').toLowerCase().includes(newFilters.field.toLowerCase()))
      );
    });
  
    setRtuSearchResult(filteredRTUs);
    setHasAppliedFilter(true);
    setMapBoundsChanged(false);

  }, [rtus]);

  // Resets the working set back to the full fleet and clears the
  // "filter applied" flag (Bugs 3172/3173/3174 guard). This is the ONLY
  // path that should flip hasAppliedFilter back to false.
  const clearFilters = useCallback(() => {
    setRtuSearchResult(rtus);
    setHasAppliedFilter(false);
    setMapBoundsChanged(false);
  }, [rtus]);

  // Distinct, sorted Location / Producer / Field values from the fleet, for filter suggestions.
  const locationOptions = useMemo(
    () => Array.from(new Set((rtus || []).map((r) => r.location).filter(Boolean) as string[])).sort(),
    [rtus]
  );
  const producerOptions = useMemo(
    () => Array.from(new Set((rtus || []).map((r) => r.provider).filter(Boolean) as string[])).sort(),
    [rtus]
  );
  const fieldOptions = useMemo(
    () => Array.from(new Set((rtus || []).map((r) => r.field).filter(Boolean) as string[])).sort(),
    [rtus]
  );


  useEffect(() => {
    const stopAnimation = (event: MouseEvent) => {
      // Check if the clicked element is an RTU card
      if (!(event.target as HTMLElement).closest('.rtu-card')) {
        setCurrentRTU(undefined);
      }
    };
  
    document.addEventListener('click', stopAnimation);
  
    return () => {
      document.removeEventListener('click', stopAnimation);
    };
  }, []);



  
  const findRTUsInBounds = useCallback((ne: any, sw: any) => {
    const hasValidBounds =
      Number.isFinite(ne?.lat) &&
      Number.isFinite(ne?.lng) &&
      Number.isFinite(sw?.lat) &&
      Number.isFinite(sw?.lng);

    if (!hasValidBounds) {
      setMapBoundsChanged(false);
      return;
    }

    // Once a filter is applied, bounds filtering must respect it — even when it
    // matched zero RTUs — rather than silently falling back to the full fleet.
    const sourceRtus = hasAppliedFilter ? rtuSearchResult : ((rtuSearchResult && rtuSearchResult.length > 0) ? rtuSearchResult : rtus);
    if (!sourceRtus || sourceRtus.length === 0) {
      setRtusInBounds([]);
      setMapBoundsChanged(false);
      return;
    }

    const rtusWithinBounds = sourceRtus.filter((rtu) => {
      if (!Number.isFinite(rtu.latitude) || !Number.isFinite(rtu.longitude)) {
        return false;
      }

      const inLatitudeRange = rtu.latitude >= sw.lat && rtu.latitude <= ne.lat;
      if (!inLatitudeRange) {
        return false;
      }

      // Handles standard ranges and anti-meridian wrapping (sw.lng > ne.lng).
      const inLongitudeRange =
        sw.lng <= ne.lng
          ? rtu.longitude >= sw.lng && rtu.longitude <= ne.lng
          : rtu.longitude >= sw.lng || rtu.longitude <= ne.lng;

      return inLongitudeRange;
    });

    setRtusInBounds(rtusWithinBounds);
    setMapBoundsChanged(rtusWithinBounds.length > 0);
    setSearchQuery(`lat between ${sw.lat} and ${ne.lat} AND lon between ${sw.lng} and ${ne.lng}`);
  }, [rtuSearchResult, rtus, hasAppliedFilter, setSearchQuery]);


  return (
    <div className="map-view" style={{ height: window.innerHeight - 50, padding: '0.25rem' }}>
      <Splitter>
        <SplitterPanel size={15}>
          <RTUFilter setFilters={setFilters} clearFilters={clearFilters} locationOptions={locationOptions} producerOptions={producerOptions} fieldOptions={fieldOptions} />
        </SplitterPanel>
        <SplitterPanel size={25}>
          <RTUList
            rtus={mapBoundsChanged && rtusInBounds.length > 0 ? rtusInBounds : rtuSearchResult}
            currentRTU={currentRTU}
            setCurrentRTU={setCurrentRTU}
            setHoveredRTU={setHoveredRTU}
            totalCount={rtus?.length || 0}
            hasAppliedFilter={hasAppliedFilter}
          />
        </SplitterPanel>
        <SplitterPanel size={60}>
          {dataLoaded ? (
            <RTUMap 
              rtus={rtuSearchResult} 
              setSearchQuery={setSearchQuery} 
              currentRTU={currentRTU} 
              setRtuSearchResult={setRtuSearchResult} 
              hoveredRTU={hoveredRTU} 
              findRTUsInBounds={findRTUsInBounds}
            />
          ) : (
            <Loader message='Getting devices data ...' />
          )}
        </SplitterPanel>
      </Splitter>
    </div>
  );
};

export default MapViewPage;
